import FuseScrollbars from '@fuse/core/FuseScrollbars';
import { styled } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Skeleton from '@mui/material/Skeleton';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import FuseNavigation from '@fuse/core/FuseNavigation';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import isUrlInChildren from '@fuse/core/FuseNavigation/isUrlInChildren';
import { Theme } from '@mui/system';
import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { navbarCloseMobile } from '@/components/theme-layouts/components/navbar/navbarSlice';
import UserMenu from '@/components/theme-layouts/components/UserMenu';
import usePathname from '@fuse/hooks/usePathname';
import useNavigate from '@fuse/hooks/useNavigate';
import useNavigation from '@/components/theme-layouts/components/navigation/hooks/useNavigation';
import {
  useGetIconQuery,
  useGetUserApplicationsQuery
} from '../../../../../../app/(control-panel)/admin/settings/SettingsApi';

const Root = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary
}));

type StyledPanelProps = {
  theme?: Theme;
  opened?: boolean;
};

const StyledPanel = styled(FuseScrollbars)<StyledPanelProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['opacity'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest
  }),
  opacity: 0,
  pointerEvents: 'none',
  minHeight: 0,
  variants: [
    {
      props: ({ opened }) => opened,
      style: {
        opacity: 1,
        pointerEvents: 'initial'
      }
    }
  ]
}));

/**
 * Check if the item needs to be opened.
 */
function needsToBeOpened(pathname: string, item: FuseNavItemType) {
  return pathname && isUrlInChildren(item, pathname);
}

type NavbarStyle3ContentProps = { className?: string; dense?: number };

/**
 * The navbar style 3 content.
 */
function NavbarStyle3Content(props: NavbarStyle3ContentProps) {
  const { data: iconUrl, isFetching } = useGetIconQuery();
  const { isSuccess: appsLoaded, isError: appsError } = useGetUserApplicationsQuery();
  const { className = '', dense = false } = props;
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { navigation } = useNavigation();
  const [selectedNavigation, setSelectedNavigation] = useState<FuseNavItemType[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const navigate = useNavigate();
  useEffect(() => {
    navigation?.forEach((item) => {
      if (needsToBeOpened(pathname, item)) {
        setSelectedNavigation([item]);
      }
    });
  }, [navigation, pathname]);

  // The right-hand panel is derived LIVE from the reactive navigation (matched by the
  // selected id) rather than the snapshot captured at click time. That way app children
  // that arrive after the menu is already open populate it without re-opening — and the
  // apps panel can show a placeholder while the applications query is still in flight.
  const selectedId = selectedNavigation[0]?.id;
  const panelNavigation = useMemo(
    () => (selectedId ? (navigation ?? []).filter((item) => item.id === selectedId) : []),
    [navigation, selectedId]
  );
  const appsPanelLoading =
    selectedId === 'applications-component' &&
    !appsLoaded &&
    !appsError &&
    (panelNavigation[0]?.children?.length ?? 0) === 0;

  function handleParentItemClick(selected: FuseNavItemType) {
    /** if there is no child item do not set/open panel
     */

    if (selected?.id !== 'applications-component' && selected?.id !== 'admin') {
      setPanelOpen(false);
      setSelectedNavigation([selected]); // Add this line
      navigate(`/${selected?.url}`);
      return;
    }

    /**
     * If navigation already selected toggle panel visibility
     */
    if (selectedNavigation[0]?.id === selected.id) {
      setPanelOpen(!panelOpen);
    } else {
      /**
       * Set navigation and open panel
       */
      setSelectedNavigation([selected]);
      setPanelOpen(true);
    }
  }

  // Open the flyout panel on hover for parent items with children (applications,
  // admin). For items without children, close the panel so a stale panel from
  // a previous hover doesn't persist while the mouse is on a leaf tab.
  function handleParentItemMouseEnter(selected: FuseNavItemType) {
    const hasChildrenPanel = selected?.id === 'applications-component' || selected?.id === 'admin';
    if (hasChildrenPanel) {
      setSelectedNavigation([selected]);
      setPanelOpen(true);
    } else {
      setPanelOpen(false);
    }
  }

  function handleChildItemClick() {
    setPanelOpen(false);
    setSelectedNavigation([]); // Clear selected navigation to properly close the submenu

    if (isMobile) {
      dispatch(navbarCloseMobile());
    }
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        setPanelOpen(false);
        setSelectedNavigation([]);
      }}
    >
      <Root
        className={clsx('flex h-full flex-auto', className)}
        onMouseLeave={() => setPanelOpen(false)}
      >
        <div
          id="fuse-navbar-side-panel"
          className="flex shrink-0 flex-col items-center h-full"
        >
          {iconUrl && <img className="my-32 w-32" src={iconUrl} alt="logo" />}

          <FuseScrollbars className="flex flex-col min-h-0 w-full flex-1 justify-start overflow-y-auto overflow-x-hidden">
            <FuseNavigation
              className={clsx('navigation shrink-0 min-h-full')}
              navigation={navigation}
              layout="vertical-2"
              onItemClick={handleParentItemClick}
              onItemMouseEnter={handleParentItemMouseEnter}
              firstLevel
              selectedId={selectedNavigation[0]?.id}
              dense={Boolean(dense)}
            />
          </FuseScrollbars>

          <div className="flex shrink-0 justify-center w-full py-16">
            <UserMenu className="" isCollapsed={Boolean(dense)} />
          </div>
        </div>

        {panelOpen && selectedNavigation.length > 0 && (
          <StyledPanel
            id="fuse-navbar-panel"
            opened={panelOpen}
            className={clsx('overflow-y-auto overflow-x-hidden shadow pt-16')}
          >
            {appsPanelLoading ? (
              <div className="flex flex-col gap-8 px-12">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rounded" height={32} />
                ))}
              </div>
            ) : (
              <FuseNavigation
                className={clsx('navigation')}
                navigation={panelNavigation}
                layout="vertical"
                onItemClick={handleChildItemClick}
              />
            )}
          </StyledPanel>
        )}
      </Root>
    </ClickAwayListener>
  );
}

export default memo(NavbarStyle3Content);
